import React, { useEffect, useState } from 'react';
import './ProductListCart.css';
import { cart, payloadCreateTransaction, TaxInvoiceInfoRequest, TaxInvoiceInfo } from '../../utils/models';
import { getAddressOptions, getUniqueProvinces, getDistricts, getSubDistricts, ThaiAddressOption } from '../../utils/thai-address';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store/store';
import { removeFromCart, setQuantity, updateQuantity } from '../../redux/actions/cartSlice';
import { useApiServices } from '../../services/endpoints/ApiEndpoint';
import Image from '../../components/image/Image';
import { useAlert } from '../../components/alert-context/AlertContext';
import Button from '../../components/button/Button';
import CustomDateTimePicker from '../../components/date-time-picker/CustomDateTimePicker';

const ProductListCart: React.FC = () => {
  const alert = useAlert();
  const navigate = useNavigate();
  const api = useApiServices()
  const dispatch = useDispatch()
  const cart = useSelector((state: RootState) => state.cart)
  const user = useSelector((state: RootState) => state.user)
  const currentLocation = useSelector((state: RootState) => state.location)
  const [activeState, setActiveState] = useState(0)
  const [tempQuantities, setTempQuantities] = useState<{ [key: string]: string }>({});
  const [selectedStoreIndex, setSelectedStoreIndex] = useState<number[]>([])
  const totalPrice = calculateTotalPrice(cart);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    address2: '',
    postalCode: '',
    dateTime: '',
  });
  const [showOptional, setShowOptional] = useState(false);
  const [isRequestTaxInvoice, setIsRequestTaxInvoice] = useState(false);
  const [useExistingTaxInfo, setUseExistingTaxInfo] = useState(false);
  const [taxInfoList, setTaxInfoList] = useState<TaxInvoiceInfo[]>([]);
  const [selectedTaxInfoId, setSelectedTaxInfoId] = useState<string>('');
  const [sendToTaxAddress, setSendToTaxAddress] = useState(false);
  const [taxFormData, setTaxFormData] = useState<TaxInvoiceInfoRequest>({
    taxpayerName: '',
    taxId: '',
    phone: '',
    address: '',
    subDistrict: '',
    district: '',
    province: '',
    postalCode: '',
  });
  const [taxDeliveryFormData, setTaxDeliveryFormData] = useState({
    name: '',
    phone: '',
    address: '',
    subDistrict: '',
    district: '',
    province: '',
    postalCode: '',
  });
  // Postal code cascade for tax invoice address fields
  const [taxAddressOptions, setTaxAddressOptions] = useState<ThaiAddressOption[]>([]);
  // Postal code cascade for tax delivery address fields
  const [taxDeliveryAddressOptions, setTaxDeliveryAddressOptions] = useState<ThaiAddressOption[]>([]);

  const handleTaxPostalCodeChange = (code: string) => {
    setTaxFormData(prev => ({ ...prev, postalCode: code, province: '', district: '', subDistrict: '' }));
    if (code.length === 5) {
      const opts = getAddressOptions(code);
      setTaxAddressOptions(opts);
      if (opts.length > 0) {
        setTaxFormData(prev => ({
          ...prev,
          postalCode: code,
          province: opts[0].province,
          district: opts[0].district,
          subDistrict: opts[0].subDistrict,
        }));
      }
    } else {
      setTaxAddressOptions([]);
    }
  };

  const handleTaxDeliveryPostalCodeChange = (code: string) => {
    setTaxDeliveryFormData(prev => ({ ...prev, postalCode: code, province: '', district: '', subDistrict: '' }));
    if (code.length === 5) {
      const opts = getAddressOptions(code);
      setTaxDeliveryAddressOptions(opts);
      if (opts.length > 0) {
        setTaxDeliveryFormData(prev => ({
          ...prev,
          postalCode: code,
          province: opts[0].province,
          district: opts[0].district,
          subDistrict: opts[0].subDistrict,
        }));
      }
    } else {
      setTaxDeliveryAddressOptions([]);
    }
  };

  // Helper function to check if two coordinates are close enough (within ~100 meters)
  const areCoordinatesClose = (lat1: number, lng1: number, lat2: number, lng2: number, threshold = 0.001) => {
    const latDiff = Math.abs(lat1 - lat2);
    const lngDiff = Math.abs(lng1 - lng2);
    return latDiff < threshold && lngDiff < threshold;
  };

  const handleSelectExistingTaxInfo = (id: string, list?: TaxInvoiceInfo[]) => {
    const source = list ?? taxInfoList;
    setSelectedTaxInfoId(id);
    const found = source.find(t => t.id === id);
    if (found) {
      setTaxFormData({
        taxpayerName: found.taxpayerName,
        taxId: found.taxId,
        phone: found.phone,
        address: found.address,
        subDistrict: found.subDistrict,
        district: found.district,
        province: found.province,
        postalCode: found.postalCode,
      });
    }
  };

  function calculateTotalPrice(cart: cart): number {
    let total = 0;
    cart.stores.forEach((store, index) => {
      if (selectedStoreIndex.includes(index)) {
        store.items.forEach(item => {
          total += (item.price || 0) * item.quantity;
        });
      }
    });
    return total;
  }

  const createTransaction = (addressId: string, taxDeliveryAddressId?: string) => {
    const payloadStores = selectedStoreIndex
      .map(index => cart.stores[index])
      .filter(store => store !== undefined);
    const payload: payloadCreateTransaction = {
      stores: payloadStores,
      addressId: addressId,
      ...(formData.dateTime ? { appointmentDateTime: formData.dateTime } : {}),
      ...(isRequestTaxInvoice ? {
        isRequestTaxInvoice: true,
        useExistingTaxInfo: useExistingTaxInfo,
        newTaxInvoiceInfo: taxFormData,
        sendToTaxAddress: sendToTaxAddress,
        ...(sendToTaxAddress ? {} : { deliveryAddressId: taxDeliveryAddressId }),
      } : {}),
      ...(selectedTaxInfoId ? { existingTaxInvoiceInfoId: selectedTaxInfoId}:{})
    }
    api.CreateTransactionAsync(payload).then(response => {
      if (response.isSuccess) {
        payloadStores.forEach(s => {
          s.items.forEach(p => {
            dispatch(removeFromCart({ productId: p.productId, storeId: s.id }))
          })
        })
        var ids = response.result.map((x: any) => x.id).join(',');
        alert.success({ message: "ขอใบเสนอราคาเรียบร้อยแล้ว", onConfirm: () => navigate(`/order/${ids}`) });
      }
    }).catch(error => {
      console.log('error', error);
    })
  }

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.address || !formData.postalCode) {
      alert.error({ message: "กรุณากรอกข้อมูลให้ครบถ้วน" });
      return;
    }
    if (isRequestTaxInvoice) {
      const { taxpayerName, taxId, phone: taxPhone, address: taxAddr, subDistrict, district, province, postalCode: taxPostal } = taxFormData;
      if (!taxpayerName || !taxId || !taxPhone || !taxAddr || !subDistrict || !district || !province || !taxPostal) {
        alert.error({ message: "กรุณากรอกข้อมูลใบกำกับภาษีให้ครบถ้วน" });
        return;
      }
      if (!sendToTaxAddress) {
        const { name: tdName, phone: tdPhone, address: tdAddr, subDistrict: tdSub, district: tdDist, province: tdProv, postalCode: tdPostal } = taxDeliveryFormData;
        if (!tdName || !tdPhone || !tdAddr || !tdSub || !tdDist || !tdProv || !tdPostal) {
          alert.error({ message: "กรุณากรอกที่อยู่จัดส่งใบกำกับภาษีให้ครบถ้วน" });
          return;
        }
      }
    }
    const payloadAddress = {
      contact: {
        name: formData.name,
        phone: formData.phone
      },
      name: formData.address,
      addressLine1: formData.address,
      addressLine2: formData.address2,
      postalCode: formData.postalCode,
      country: 'Thailand',
      latitude: currentLocation.lat,
      longitude: currentLocation.lng,
    }
    const { isSuccess, result } = await api.AddAddressAsync(user?.entityUniqueIdentifierId || '', payloadAddress);
    if (!isSuccess) {
      alert.error({ message: "บางอย่างผิดพลาด กรุณาลองใหม่อีกครั้ง" });
      return;
    }
    const mainAddressId: string = result.id;

    if (isRequestTaxInvoice && !sendToTaxAddress) {
      const taxDeliveryPayload = {
        contact: { name: taxDeliveryFormData.name, phone: taxDeliveryFormData.phone },
        name: taxDeliveryFormData.address,
        addressLine1: `${taxDeliveryFormData.address} ${taxDeliveryFormData.subDistrict} ${taxDeliveryFormData.district} ${taxDeliveryFormData.province}`,
        addressLine2: '',
        postalCode: taxDeliveryFormData.postalCode,
        country: 'Thailand',
        latitude: currentLocation.lat,
        longitude: currentLocation.lng,
      };
      const { isSuccess: tdOk, result: tdResult } = await api.AddAddressAsync(user?.entityUniqueIdentifierId || '', taxDeliveryPayload);
      if (!tdOk) {
        alert.error({ message: "บางอย่างผิดพลาด กรุณาลองใหม่อีกครั้ง" });
        return;
      }
      createTransaction(mainAddressId, tdResult.id);
    } else {
      createTransaction(mainAddressId);
    }
  }

  useEffect(() => {
    if (cart.stores) {
      const allIndexes = cart.stores.map((_, index) => index);
      setSelectedStoreIndex(allIndexes);
    }
  }, [])

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.firstName || '',
        phone: user.phone || '',
      }))
    }
  }, [user])

  // Fetch address history and auto-fill if location matches
  useEffect(() => {
    const fetchAddressHistory = async () => {
      if (!user?.entityUniqueIdentifierId || !currentLocation.lat || !currentLocation.lng) {
        return;
      }

      try {
        const { isSuccess, result } = await api.GetAddressesAsync(user.entityUniqueIdentifierId);
        if (isSuccess && Array.isArray(result) && result.length > 0) {
          // Find address with matching location
          const matchingAddress = result.find((addr: any) =>
            areCoordinatesClose(
              currentLocation.lat,
              currentLocation.lng,
              addr.latitude,
              addr.longitude
            )
          );

          if (matchingAddress) {
            // Auto-fill form with matching address
            setFormData(prev => ({
              ...prev,
              name: matchingAddress.contact?.name || prev.name,
              phone: matchingAddress.contact?.phone || prev.phone,
              address: matchingAddress.addressLine1 || '',
              address2: matchingAddress.addressLine2 || '',
              postalCode: matchingAddress.postalCode || '',
            }));

            // Show optional fields if address2 exists
            if (matchingAddress.addressLine2) {
              setShowOptional(true);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching address history:', error);
      }
    };

    fetchAddressHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.entityUniqueIdentifierId, currentLocation.lat, currentLocation.lng]);

  // Fetch saved tax invoice infos
  useEffect(() => {
    const fetchTaxInfos = async () => {
      if (!user?.entityUniqueIdentifierId) return;
      try {
        const { isSuccess, result } = await api.GetTaxInvoiceInfosAsync(user.id);
        if (isSuccess && Array.isArray(result) && result.length > 0) {
          setTaxInfoList(result);
          setUseExistingTaxInfo(true);
          handleSelectExistingTaxInfo(result[0].id, result);
        }
      } catch (error) {
        console.error('Error fetching tax infos:', error);
      }
    };
    fetchTaxInfos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.entityUniqueIdentifierId]);

  const selectedCount = selectedStoreIndex.reduce((count, idx) => {
    return count + (cart.stores[idx]?.items?.length || 0);
  }, 0);

  return (
    <section className="product-list-cart-wrapper lg:container lg:mx-auto">

      {/* Step Indicator */}
      {cart.totalItem > 0 && (
        <div className="cart-step-indicator">
          <div className={`step-item ${activeState >= 0 ? 'active' : ''}`}>
            <div className="step-circle">1</div>
            <span className="step-label">ตะกร้าสินค้า</span>
          </div>
          <div className={`step-line ${activeState >= 1 ? 'active' : ''}`}></div>
          <div className={`step-item ${activeState >= 1 ? 'active' : ''}`}>
            <div className="step-circle">2</div>
            <span className="step-label">ข้อมูลการจัดส่ง</span>
          </div>
        </div>
      )}

      {/* Step 1: Product List */}
      {activeState === 0 && (
        <div className="block-product-items-list">
          {cart?.stores?.map((store, index) => (
            <div key={index} className="product-item mb-4">
              <div className="item-title">
                <input
                  type="checkbox"
                  className="checkbox title-checkbox"
                  value={index}
                  checked={selectedStoreIndex.includes(index)}
                  onChange={() => {
                    if (selectedStoreIndex.includes(index)) {
                      setSelectedStoreIndex(selectedStoreIndex.filter(i => i !== index));
                    } else {
                      setSelectedStoreIndex([...selectedStoreIndex, index]);
                    }
                  }}
                />
                <h2 className="title-name">{store.name}</h2>
              </div>
              <div className="items-list">
                {store.items.map((item, itemIdx) => (
                  <div key={itemIdx} className="items-list-detail">
                    <div className="detail-image">
                      <Image id={item?.productId || ''} alt="no-image-product" className="w-full rounded-xl" />
                    </div>
                    <div className="detail-content min-w-0">
                      <p className="content-name truncate">{item.product?.nameTh}</p>
                      <div className="block-number-products">
                        <div className="number-products">
                          <label className="input">
                            <button
                              disabled={item.quantity <= 1}
                              className="btn decrease-number"
                              onClick={() => dispatch(updateQuantity({ storeId: store.id || '', productId: item.productId, change: -1 }))}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              placeholder="999"
                              className="input input-number-products"
                              value={tempQuantities[`${store.id}_${item.productId}`] ?? item.quantity.toString()}
                              onChange={(e) => {
                                setTempQuantities({
                                  ...tempQuantities,
                                  [`${store.id}_${item.productId}`]: e.target.value,
                                });
                              }}
                              onBlur={() => {
                                const newQty = parseInt(tempQuantities[`${store.id}_${item.productId}`]);
                                if (!isNaN(newQty) && newQty > 0) {
                                  dispatch(setQuantity({
                                    storeId: store.id || '',
                                    productId: item.productId,
                                    quantity: newQty
                                  }));
                                }
                                setTempQuantities(prev => {
                                  const copy = { ...prev };
                                  delete copy[`${store.id}_${item.productId}`];
                                  return copy;
                                });
                              }}
                            />
                            <button
                              disabled={item.quantity >= 9999}
                              className="btn increase-number"
                              onClick={() => dispatch(updateQuantity({ storeId: store.id || '', productId: item.productId, change: +1 }))}
                            >
                              +
                            </button>
                          </label>
                        </div>
                      </div>
                      <p className="product-price-unit">{item.price?.toLocaleString()} บาท</p>
                    </div>
                    <div className="items-list-btn">
                      <Button variant="icon" onClick={() => {
                        dispatch(removeFromCart({ productId: item.productId, storeId: store.id }))
                      }}>
                        <img src="/assets/icons/icon-delete.svg" alt="icon-delete" className="icon icon-delete" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {cart.totalItem > 0 && (
            <div className="cart-summary-bar">
              <div className="summary-info">
                <span className="summary-count">{selectedCount} รายการ</span>
                <span className="summary-total">{totalPrice.toLocaleString()} บาท</span>
              </div>
              <p className="summary-note">ยังไม่รวมค่าขนส่ง</p>
            </div>
          )}
        </div>
      )}

      {/* Empty cart */}
      {cart.totalItem <= 0 && (
        <div className="empty-cart">
          <img src="/assets/icons/icon-cart.svg" alt="empty-cart" className="empty-cart-icon" />
          <p className="empty-cart-text">ยังไม่มีสินค้าในตะกร้า</p>
          <Button variant="primary" onClick={() => navigate('/')}>เลือกสินค้า</Button>
        </div>
      )}

      {/* Step 1 CTA */}
      {cart.totalItem > 0 && selectedStoreIndex.length > 0 && activeState === 0 && (
        <div className="block-btn-check-out">
          <Button
            onClick={() => setActiveState(1)}
            variant="primary"
            fullWidth
            className="max-w-[21.5rem]"
          >
            ขอใบเสนอราคา ({selectedCount} รายการ)
          </Button>
        </div>
      )}

      {/* Step 2: Delivery Info */}
      {activeState === 1 && (
        <div className="block-check-out">
          <div className="checkout-section-title">
            <h3>ข้อมูลผู้รับ</h3>
          </div>

          {/* Contact Info */}
          <div className="checkout-card">
            <div className="form-row">
              <label className="form-control">
                <div className="label">
                  <span className="label-text required">ชื่อผู้รับ</span>
                </div>
                <input
                  type="text"
                  placeholder="กรอกชื่อ-นามสกุล"
                  className="input input-bordered"
                  name='name'
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </label>
              <label className="form-control">
                <div className="label">
                  <span className="label-text required">เบอร์ติดต่อ</span>
                </div>
                <input
                  type="tel"
                  placeholder="0xx-xxx-xxxx"
                  className="input input-bordered"
                  name='phone'
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </label>
            </div>
          </div>

          <div className="checkout-section-title">
            <h3>ที่จัดส่งสินค้า</h3>
          </div>

          <div className="checkout-card">
            <label className="form-control">
              <div className="label">
                <span className="label-text required">ที่อยู่</span>
              </div>
              <input
                type="text"
                placeholder="บ้านเลขที่ ซอย ถนน แขวง เขต"
                className="input input-bordered"
                name='address'
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </label>

            <label className="form-control">
              <div className="label">
                <span className="label-text required">รหัสไปรษณีย์</span>
              </div>
              <input
                type="text"
                placeholder="เช่น 10110"
                className="input input-bordered"
                name='postalCode'
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                maxLength={5}
              />
            </label>

            {/* Optional fields toggle */}
            <button
              type="button"
              className="optional-toggle"
              onClick={() => setShowOptional(!showOptional)}
            >
              <span>{showOptional ? 'ซ่อนข้อมูลเพิ่มเติม' : '+ เพิ่มข้อมูลเพิ่มเติม (ไม่บังคับ)'}</span>
              <img
                src="/assets/icons/icon-next-orange.svg"
                alt="toggle"
                className={`optional-toggle-icon ${showOptional ? 'rotate-90' : ''}`}
              />
            </button>

            {showOptional && (
              <div className="optional-fields">
                <label className="form-control">
                  <div className="label">
                    <span className="label-text">ที่อยู่เพิ่มเติม <span className="optional-badge">ไม่บังคับ</span></span>
                  </div>
                  <input
                    type="text"
                    placeholder="หมู่บ้าน อาคาร ชั้น ฯลฯ"
                    className="input input-bordered"
                    name='address2'
                    value={formData.address2}
                    onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                  />
                </label>

                <label className="form-control">
                  <div className="label">
                    <span className="label-text">นัดวันจัดส่ง <span className="optional-badge">ไม่บังคับ</span></span>
                  </div>
                  <CustomDateTimePicker minDate={new Date()} onChange={(dt) => setFormData({ ...formData, dateTime: dt })} />
                </label>
              </div>
            )}
          </div>

          {/* Tax Invoice Section */}
          <div className="checkout-section-title">
            <h3>ใบกำกับภาษี</h3>
          </div>
          <div className="checkout-card">
            <label className="tax-toggle-row">
              <input
                type="checkbox"
                className="checkbox border-brand [--chkbg:#FD6B35] [--chkfg:#FFFFFF]"
                checked={isRequestTaxInvoice}
                onChange={(e) => setIsRequestTaxInvoice(e.target.checked)}
              />
              <span className="tax-toggle-label">ต้องการรับใบกำกับภาษี</span>
            </label>

            {isRequestTaxInvoice && (
              <div className="tax-invoice-fields">
                {taxInfoList.length > 0 && (
                  <label className="tax-toggle-row">
                    <input
                      type="checkbox"
                      className="checkbox border-brand [--chkbg:#FD6B35] [--chkfg:#FFFFFF]"
                      checked={useExistingTaxInfo}
                      onChange={(e) => {
                        setUseExistingTaxInfo(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedTaxInfoId('');
                          setTaxFormData({ taxpayerName: '', taxId: '', phone: '', address: '', subDistrict: '', district: '', province: '', postalCode: '' });
                        } else if (taxInfoList.length > 0) {
                          handleSelectExistingTaxInfo(taxInfoList[0].id);
                        }
                      }}
                    />
                    <span className="tax-toggle-label">ใช้ข้อมูลที่บันทึกไว้</span>
                  </label>
                )}

                {useExistingTaxInfo && taxInfoList.length > 0 && (
                  <select
                    className="select select-bordered w-full"
                    value={selectedTaxInfoId}
                    onChange={(e) => handleSelectExistingTaxInfo(e.target.value)}
                  >
                    {taxInfoList.map((info) => (
                      <option key={info.id} value={info.id}>
                        {info.taxpayerName} ({info.taxId})
                      </option>
                    ))}
                  </select>
                )}

                <label className="form-control">
                  <div className="label"><span className="label-text required">ชื่อผู้เสียภาษี / ชื่อบริษัท</span></div>
                  <input
                    type="text"
                    placeholder="ชื่อบริษัท หรือ ชื่อ-นามสกุล"
                    className="input input-bordered"
                    value={taxFormData.taxpayerName}
                    onChange={(e) => setTaxFormData({ ...taxFormData, taxpayerName: e.target.value })}
                    maxLength={200}
                  />
                </label>

                <label className="form-control">
                  <div className="label"><span className="label-text required">เลขประจำตัวผู้เสียภาษี</span></div>
                  <input
                    type="text"
                    placeholder="13 หลัก"
                    className="input input-bordered"
                    value={taxFormData.taxId}
                    onChange={(e) => setTaxFormData({ ...taxFormData, taxId: e.target.value })}
                    maxLength={13}
                  />
                </label>

                <label className="form-control">
                  <div className="label"><span className="label-text required">เบอร์ติดต่อ</span></div>
                  <input
                    type="tel"
                    placeholder="0xx-xxx-xxxx"
                    className="input input-bordered"
                    value={taxFormData.phone}
                    onChange={(e) => setTaxFormData({ ...taxFormData, phone: e.target.value })}
                    maxLength={10}
                  />
                </label>

                <label className="form-control">
                  <div className="label"><span className="label-text required">ที่อยู่</span></div>
                  <input
                    type="text"
                    placeholder="บ้านเลขที่ ซอย ถนน"
                    className="input input-bordered"
                    value={taxFormData.address}
                    onChange={(e) => setTaxFormData({ ...taxFormData, address: e.target.value })}
                    maxLength={500}
                  />
                </label>

                <label className="form-control">
                  <div className="label"><span className="label-text required">รหัสไปรษณีย์ (ที่อยู่ภาษี)</span></div>
                  <input
                    type="text"
                    placeholder="เช่น 10110"
                    className="input input-bordered"
                    value={taxFormData.postalCode}
                    onChange={(e) => handleTaxPostalCodeChange(e.target.value)}
                    maxLength={5}
                  />
                </label>

                {taxAddressOptions.length > 0 && (
                  <>
                    <label className="form-control">
                      <div className="label"><span className="label-text required">จังหวัด</span></div>
                      <select
                        className="select select-bordered w-full"
                        value={taxFormData.province}
                        onChange={(e) => {
                          setTaxFormData(prev => ({ ...prev, province: e.target.value, district: '', subDistrict: '' }));
                        }}
                      >
                        {getUniqueProvinces(taxFormData.postalCode).map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </label>

                    <label className="form-control">
                      <div className="label"><span className="label-text required">เขต/อำเภอ</span></div>
                      <select
                        className="select select-bordered w-full"
                        value={taxFormData.district}
                        onChange={(e) => {
                          const chosenDist = e.target.value;
                          const match = taxAddressOptions.find(o => o.district === chosenDist);
                          setTaxFormData(prev => ({
                            ...prev,
                            district: chosenDist,
                            province: match?.province ?? prev.province,
                            subDistrict: '',
                          }));
                        }}
                      >
                        {getDistricts(taxFormData.postalCode, taxFormData.province).map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </label>

                    <label className="form-control">
                      <div className="label"><span className="label-text required">แขวง/ตำบล</span></div>
                      <select
                        className="select select-bordered w-full"
                        value={taxFormData.subDistrict}
                        onChange={(e) => {
                          const chosenSub = e.target.value;
                          const match = taxAddressOptions.find(o => o.subDistrict === chosenSub);
                          setTaxFormData(prev => ({
                            ...prev,
                            subDistrict: chosenSub,
                            district: match?.district ?? prev.district,
                            province: match?.province ?? prev.province,
                          }));
                        }}
                      >
                        {getSubDistricts(taxFormData.postalCode, taxFormData.province, taxFormData.district).map(sub => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    </label>
                  </>
                )}

                {taxAddressOptions.length === 0 && taxFormData.postalCode.length === 5 && (
                  <>
                    <label className="form-control">
                      <div className="label"><span className="label-text required">จังหวัด</span></div>
                      <input type="text" placeholder="จังหวัด" className="input input-bordered" value={taxFormData.province}
                        onChange={(e) => setTaxFormData({ ...taxFormData, province: e.target.value })} maxLength={100} />
                    </label>
                    <div className="form-row">
                      <label className="form-control">
                        <div className="label"><span className="label-text required">เขต/อำเภอ</span></div>
                        <input type="text" placeholder="เขต/อำเภอ" className="input input-bordered" value={taxFormData.district}
                          onChange={(e) => setTaxFormData({ ...taxFormData, district: e.target.value })} maxLength={100} />
                      </label>
                      <label className="form-control">
                        <div className="label"><span className="label-text required">แขวง/ตำบล</span></div>
                        <input type="text" placeholder="แขวง/ตำบล" className="input input-bordered" value={taxFormData.subDistrict}
                          onChange={(e) => setTaxFormData({ ...taxFormData, subDistrict: e.target.value })} maxLength={100} />
                      </label>
                    </div>
                  </>
                )}

                <label className="tax-toggle-row">
                  <input
                    type="checkbox"
                    className="checkbox border-brand [--chkbg:#FD6B35] [--chkfg:#FFFFFF]"
                    checked={sendToTaxAddress}
                    onChange={(e) => setSendToTaxAddress(e.target.checked)}
                  />
                  <span className="tax-toggle-label">จัดส่งไปยังที่อยู่ในใบกำกับภาษี</span>
                </label>

                {!sendToTaxAddress && (
                  <div className="tax-delivery-fields">
                    <p className="tax-delivery-title">ที่จัดส่งใบกำกับภาษี</p>

                    <div className="form-row">
                      <label className="form-control">
                        <div className="label"><span className="label-text required">ชื่อผู้รับ</span></div>
                        <input type="text" placeholder="ชื่อ-นามสกุล" className="input input-bordered"
                          value={taxDeliveryFormData.name}
                          onChange={(e) => setTaxDeliveryFormData({ ...taxDeliveryFormData, name: e.target.value })} />
                      </label>
                      <label className="form-control">
                        <div className="label"><span className="label-text required">เบอร์ติดต่อ</span></div>
                        <input type="tel" placeholder="0xx-xxx-xxxx" className="input input-bordered"
                          value={taxDeliveryFormData.phone}
                          onChange={(e) => setTaxDeliveryFormData({ ...taxDeliveryFormData, phone: e.target.value })} maxLength={10} />
                      </label>
                    </div>

                    <label className="form-control">
                      <div className="label"><span className="label-text required">ที่อยู่</span></div>
                      <input type="text" placeholder="บ้านเลขที่ ซอย ถนน" className="input input-bordered"
                        value={taxDeliveryFormData.address}
                        onChange={(e) => setTaxDeliveryFormData({ ...taxDeliveryFormData, address: e.target.value })} maxLength={500} />
                    </label>

                    <label className="form-control">
                      <div className="label"><span className="label-text required">รหัสไปรษณีย์</span></div>
                      <input type="text" placeholder="เช่น 10110" className="input input-bordered"
                        value={taxDeliveryFormData.postalCode}
                        onChange={(e) => handleTaxDeliveryPostalCodeChange(e.target.value)} maxLength={5} />
                    </label>

                    {taxDeliveryAddressOptions.length > 0 && (
                      <>
                        <label className="form-control">
                          <div className="label"><span className="label-text required">จังหวัด</span></div>
                          <select className="select select-bordered w-full" value={taxDeliveryFormData.province}
                            onChange={(e) => {
                              setTaxDeliveryFormData(prev => ({ ...prev, province: e.target.value, district: '', subDistrict: '' }));
                            }}
                          >
                            {getUniqueProvinces(taxDeliveryFormData.postalCode).map(p => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </label>

                        <label className="form-control">
                          <div className="label"><span className="label-text required">เขต/อำเภอ</span></div>
                          <select className="select select-bordered w-full" value={taxDeliveryFormData.district}
                            onChange={(e) => {
                              const chosenDist = e.target.value;
                              const match = taxDeliveryAddressOptions.find(o => o.district === chosenDist);
                              setTaxDeliveryFormData(prev => ({
                                ...prev,
                                district: chosenDist,
                                province: match?.province ?? prev.province,
                                subDistrict: '',
                              }));
                            }}
                          >
                            {getDistricts(taxDeliveryFormData.postalCode, taxDeliveryFormData.province).map(d => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </select>
                        </label>

                        <label className="form-control">
                          <div className="label"><span className="label-text required">แขวง/ตำบล</span></div>
                          <select className="select select-bordered w-full" value={taxDeliveryFormData.subDistrict}
                            onChange={(e) => {
                              const chosenSub = e.target.value;
                              const match = taxDeliveryAddressOptions.find(o => o.subDistrict === chosenSub);
                              setTaxDeliveryFormData(prev => ({
                                ...prev,
                                subDistrict: chosenSub,
                                district: match?.district ?? prev.district,
                                province: match?.province ?? prev.province,
                              }));
                            }}
                          >
                            {getSubDistricts(taxDeliveryFormData.postalCode, taxDeliveryFormData.province, taxDeliveryFormData.district).map(sub => (
                              <option key={sub} value={sub}>{sub}</option>
                            ))}
                          </select>
                        </label>
                      </>
                    )}

                    {taxDeliveryAddressOptions.length === 0 && taxDeliveryFormData.postalCode.length === 5 && (
                      <>
                        <label className="form-control">
                          <div className="label"><span className="label-text required">จังหวัด</span></div>
                          <input type="text" placeholder="จังหวัด" className="input input-bordered"
                            value={taxDeliveryFormData.province}
                            onChange={(e) => setTaxDeliveryFormData({ ...taxDeliveryFormData, province: e.target.value })} maxLength={100} />
                        </label>
                        <div className="form-row">
                          <label className="form-control">
                            <div className="label"><span className="label-text required">เขต/อำเภอ</span></div>
                            <input type="text" placeholder="เขต/อำเภอ" className="input input-bordered"
                              value={taxDeliveryFormData.district}
                              onChange={(e) => setTaxDeliveryFormData({ ...taxDeliveryFormData, district: e.target.value })} maxLength={100} />
                          </label>
                          <label className="form-control">
                            <div className="label"><span className="label-text required">แขวง/ตำบล</span></div>
                            <input type="text" placeholder="แขวง/ตำบล" className="input input-bordered"
                              value={taxDeliveryFormData.subDistrict}
                              onChange={(e) => setTaxDeliveryFormData({ ...taxDeliveryFormData, subDistrict: e.target.value })} maxLength={100} />
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Order summary */}
          <div className="checkout-summary">
            <div className="summary-row">
              <span>รายการที่เลือก</span>
              <span>{selectedCount} รายการ</span>
            </div>
            <div className="summary-row total">
              <span>ราคารวม</span>
              <span className="price">{totalPrice.toLocaleString()} บาท</span>
            </div>
            <p className="summary-note-bottom">ราคาขนส่งจะแจ้งในใบเสนอราคา</p>
          </div>

          <div className="block-btn-check-out">
            <Button
              onClick={(e) => handleSubmit(e)}
              variant="primary"
              fullWidth
              className="max-w-[21.5rem] mb-3"
            >
              ยืนยันคำขอ
            </Button>
            <Button
              onClick={() => setActiveState(0)}
              variant="secondary"
              fullWidth
              className="max-w-[21.5rem]"
            >
              ย้อนกลับ
            </Button>
          </div>
        </div>
      )}

    </section>
  )
}

export default ProductListCart
